import type { ParticipantConnectionStats } from "./types";

type StatsRecord = Record<string, unknown>;

export type StatsRole = "sender" | "receiver";

function readNumber(
  record: StatsRecord | undefined,
  key: string,
): number | null {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readString(
  record: StatsRecord | undefined,
  key: string,
): string | null {
  const value = record?.[key];
  return typeof value === "string" ? value : null;
}

/**
 * Converts RTCPeerConnection.getStats() output into the values shown in the
 * UI. Everything here comes from the browser; no synthetic placeholders are
 * substituted for missing measurements.
 */
export class StatsCollector {
  private readonly pc: RTCPeerConnection;
  private readonly role: StatsRole;
  private lastBytes: number | null = null;
  private lastTimestamp: number | null = null;

  constructor(pc: RTCPeerConnection, role: StatsRole) {
    this.pc = pc;
    this.role = role;
  }

  reset(): void {
    this.lastBytes = null;
    this.lastTimestamp = null;
  }

  async sample(now = performance.now()): Promise<ParticipantConnectionStats> {
    const report = await this.pc.getStats();
    const records = new Map<string, StatsRecord>();
    report.forEach((stat, id) => {
      records.set(id, stat as unknown as StatsRecord);
    });

    let inboundVideo: StatsRecord | undefined;
    let outboundVideo: StatsRecord | undefined;
    let remoteInboundVideo: StatsRecord | undefined;
    let mediaSource: StatsRecord | undefined;
    let selectedPair: StatsRecord | undefined;

    for (const record of records.values()) {
      const type = readString(record, "type");
      const kind =
        readString(record, "kind") ?? readString(record, "mediaType");
      if (type === "inbound-rtp" && kind === "video") inboundVideo = record;
      if (type === "outbound-rtp" && kind === "video") outboundVideo = record;
      if (type === "remote-inbound-rtp" && kind === "video")
        remoteInboundVideo = record;
      if (type === "media-source" && kind === "video") mediaSource = record;
      if (type === "candidate-pair") {
        const selected = record.selected === true;
        const succeeded = readString(record, "state") === "succeeded";
        const nominated = record.nominated === true;
        if (selected || (succeeded && nominated && !selectedPair)) {
          selectedPair = record;
        }
      }
    }

    let localCandidate: StatsRecord | undefined;
    let remoteCandidate: StatsRecord | undefined;
    if (selectedPair) {
      const localId = readString(selectedPair, "localCandidateId");
      const remoteId = readString(selectedPair, "remoteCandidateId");
      if (localId) localCandidate = records.get(localId);
      if (remoteId) remoteCandidate = records.get(remoteId);
    }

    const candidateType = (record: StatsRecord | undefined) =>
      readString(record, "candidateType");
    const localType = candidateType(localCandidate);
    const remoteType = candidateType(remoteCandidate);
    const transport: ParticipantConnectionStats["transport"] =
      localType === "relay" || remoteType === "relay"
        ? "relay"
        : localType || remoteType
          ? "p2p"
          : "unknown";

    const inbound = this.role === "receiver" ? inboundVideo : undefined;
    const outbound = this.role === "sender" ? outboundVideo : undefined;
    const remoteInbound =
      this.role === "sender" ? remoteInboundVideo : undefined;

    const width =
      readNumber(outbound ?? inbound, "frameWidth") ??
      readNumber(mediaSource, "width");
    const height =
      readNumber(outbound ?? inbound, "frameHeight") ??
      readNumber(mediaSource, "height");
    const fps =
      readNumber(outbound ?? inbound, "framesPerSecond") ??
      readNumber(mediaSource, "framesPerSecond");

    const bytes =
      this.role === "sender"
        ? (readNumber(outbound, "bytesSent") ?? readNumber(outbound, "bytes"))
        : (readNumber(inbound, "bytesReceived") ??
          readNumber(inbound, "bytes"));

    let bitrateKbps: number | null = null;
    if (bytes != null) {
      if (
        this.lastBytes != null &&
        this.lastTimestamp != null &&
        now > this.lastTimestamp
      ) {
        const deltaBits = Math.max(0, bytes - this.lastBytes) * 8;
        const deltaSeconds = (now - this.lastTimestamp) / 1000;
        bitrateKbps = deltaBits / deltaSeconds / 1000;
      }
      this.lastBytes = bytes;
      this.lastTimestamp = now;
    }

    const packetsLost = readNumber(inbound, "packetsLost");
    const packetsReceived = readNumber(inbound, "packetsReceived");
    const fractionLost = readNumber(remoteInbound, "fractionLost");
    let packetLossPercent: number | null = null;
    if (this.role === "receiver") {
      if (packetsLost != null && packetsReceived != null) {
        const total = packetsLost + packetsReceived;
        packetLossPercent = total > 0 ? (packetsLost / total) * 100 : 0;
      }
    } else if (fractionLost != null) {
      packetLossPercent = fractionLost * 100;
    } else if (packetsLost != null) {
      packetLossPercent = null;
    }

    const rttSeconds = readNumber(selectedPair, "currentRoundTripTime");
    const remoteRttSeconds = readNumber(remoteInbound, "roundTripTime");
    const rttMs =
      rttSeconds != null
        ? rttSeconds * 1000
        : remoteRttSeconds != null
          ? remoteRttSeconds * 1000
          : null;

    const jitterSeconds =
      readNumber(inbound, "jitter") ?? readNumber(remoteInbound, "jitter");
    const jitterMs = jitterSeconds != null ? jitterSeconds * 1000 : null;

    const framesDropped = readNumber(inbound, "framesDropped");

    return {
      width,
      height,
      fps,
      bitrateKbps,
      rttMs,
      packetLossPercent,
      jitterMs,
      framesDropped,
      transport,
      packetsReceived,
      packetsLost: packetsLost ?? null,
      bytes,
    };
  }
}

export function startStatsSampler(
  pc: RTCPeerConnection,
  role: StatsRole,
  onSample: (stats: ParticipantConnectionStats) => void,
  onError?: (error: unknown) => void,
  intervalMs = 1000,
): () => void {
  const collector = new StatsCollector(pc, role);
  let stopped = false;
  let timer: number | null = null;

  const tick = async () => {
    if (stopped) return;
    try {
      const stats = await collector.sample();
      if (!stopped) onSample(stats);
    } catch (error) {
      if (!stopped) onError?.(error);
    }
    if (!stopped) {
      timer = window.setTimeout(() => {
        void tick();
      }, intervalMs);
    }
  };

  void tick();

  return () => {
    stopped = true;
    if (timer != null) window.clearTimeout(timer);
  };
}
